import { XClose } from "@untitledui/icons";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Icon, type IconComponent } from "../Icon";
import styles from "./Chip.module.css";

/**
 * Chip — pill-shaped tag/label primitive.
 *
 * Matches the Figma Chip / Badge component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1110:8302). A compact, rounded pill
 * used for tags, filters, status labels, and similar low-weight
 * indicators. Sibling primitive to StatusDot — but Chip carries a
 * text label, optional dot + icon, and an optional dismiss affordance.
 *
 * Tones (from the Figma `color` enum):
 *   - `success` — green. Maps to --text-success for the label, dot, and
 *                 icons; filled background uses a green-50 tint.
 *   - `negative` — red. Uses --text-negative semantic (Figma's red-600
 *                 primitive has no semantic alias today; --text-negative
 *                 is close enough for the single-red use case — see the
 *                 design-asks doc entry "--chip-negative-text").
 *
 * `filled` (default: true, matches Figma's `bg=true` default):
 *   - When true, the chip paints a soft tinted background matching
 *     its tone.
 *   - When false, the chip is transparent — only the text, dot, and
 *     icons carry color.
 *
 * Optional content:
 *   - `dot` — 5×5px colored dot at the start. Often used for status
 *     chips where the label describes the state.
 *   - `leadingIcon` — 14×14px glyph before the label (e.g. a `+` for
 *     a "Add filter" action).
 *   - `onDismiss` — if provided, renders an X button after the label
 *     that calls the handler on click. `dismissLabel` is REQUIRED when
 *     `onDismiss` is set for screen-reader accessibility.
 *
 * Accessibility:
 *   - Root is a `<span>` (non-interactive). The dismiss X is a nested
 *     `<button>` with its own aria-label.
 *   - The dot and leading icon are decorative (`aria-hidden`) — the
 *     visible text label is the accessible name for the chip.
 */

type Tone = "success" | "negative";

// Discriminated union on `onDismiss` so `dismissLabel` is required
// whenever the dismiss button is rendered, and omitted otherwise.
type DismissProps =
  | { onDismiss?: undefined; dismissLabel?: undefined }
  | { onDismiss: () => void; dismissLabel: string };

export type ChipProps = {
  children: ReactNode;
  /** Color tone. Defaults to `success`. */
  tone?: Tone;
  /** Paints a tinted background matching the tone. Defaults to `true`. */
  filled?: boolean;
  /** Renders a 5×5px colored dot before the label. */
  dot?: boolean;
  /** Optional 14×14px leading icon before the label. */
  leadingIcon?: IconComponent;
  className?: string;
} & DismissProps &
  Omit<ComponentPropsWithoutRef<"span">, "className" | "children" | "onClick">;

export function Chip({
  children,
  tone = "success",
  filled = true,
  dot,
  leadingIcon,
  onDismiss,
  dismissLabel,
  className,
  ...rest
}: ChipProps) {
  const classes = [
    styles.chip,
    styles[`tone-${tone}`],
    filled ? styles.filled : styles.unfilled,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes} {...rest}>
      {dot && <span className={styles.dot} aria-hidden="true" />}
      {leadingIcon && <Icon as={leadingIcon} size="xs" tone="inherit" />}
      <span className={styles.label}>{children}</span>
      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          aria-label={dismissLabel}
          onClick={onDismiss}
        >
          <Icon as={XClose} size="xs" tone="inherit" />
        </button>
      )}
    </span>
  );
}
